import { useEffect, useRef, useState } from "react";

import AnchorCheckbox from "./AnchorCheckbox";
import Autocomplete from "./Autocomplete";
import SpeechComponent from "./Speech";

import { useConfig } from "../../data/useConfig";
import { useFilters } from "../../data/useFilters";

import searchApiService from "../../service/searchApiService";

import {
  dispatchGA4SearchEvent,
  dispatchInputInitializedEvent,
} from "../../utils/events";
import {
  filterRedirectSuggestions,
  findRedirectRule,
} from "../../utils/redirect";

import {
  CustomForm,
  CustomInput,
  CustomSearchIcon,
  CustomClearIcon,
  InputContainer,
} from "./styles";

const noTermStyle = {
  width: "auto",
  marginLeft: "auto",
  marginRight: "auto",
  maxWidth: "60vw",
  height: "60px",
};

const BNLY_SEARCH_HISTORY = "bnly-search-history";

const SearchInput = ({
  onSubmit = false,
  noTerm,
  detachedAutocomplete = false,
}) => {
  const {
    anchorsApplied,
    setAnchorsApplied,
    useAnchors,
    setUseAnchors,
    setAnchor,
    search,
    setSearch,
  } = useFilters();

  const {
    searchKey,
    search_page_path,
    tenant_id,
    autocompleteConfigs,
    inputConfigs = {},
    voiceConfigs,
    redirectRules,
  } = useConfig();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const searchInput = urlParams.get(searchKey);
  const inputRef = useRef(null);
  const [input, setInput] = useState(search?.input || searchInput);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestedTerms, setSuggestedTerms] = useState([]);

  const { placeholder } = inputConfigs;

  useEffect(() => {
    document.addEventListener("bnly.search.input.clean", () => setInput(""));
    dispatchInputInitializedEvent();
  }, []);

  useEffect(() => {
    const setupAnchorsEvent = (event) => {
      event.preventDefault();
      const details = event.detail || event.details || {};

      if (details?.data?.anchors_applied?.length > 0) {
        setAnchorsApplied(details.data.anchors_applied);

        if (details.data.anchors_applied[0].checked) {
          setUseAnchors(true);
        } else {
          setUseAnchors(false);
        }
      } else {
        setAnchorsApplied([]);
        setUseAnchors(false);
      }
    };

    document.removeEventListener(
      "beon.search.setup.anchors",
      setupAnchorsEvent
    );

    document.addEventListener("beon.search.setup.anchors", setupAnchorsEvent);
  }, []);

  useEffect(() => {
    if (noTerm) return;
    setTimeout(() => {
      if (
        window.bn_config_search_page.search_term ||
        window.bn_config_search_page.anchors
      ) {
        setInput(
          decodeURIComponent(search.input) ||
            window.bn_config_search_page.search_term
        );

        onSearch(
          null,
          window.bn_config_search_page.search_term,
          window.bn_config_search_page.anchors || ""
        );
      }
    }, 300);
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (inputRef?.current?.contains(event.target)) {
        setIsFocused(true);
      } else {
        setIsFocused(false);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    const longSearchWordCount = 3;
    const inputContainers = document.querySelectorAll(
      ".bnly-search__input-container"
    );

    inputContainers.forEach((container) => {
      const searchInput = container.querySelector(".bn-search__input");

      if (searchInput) {
        const trimmed = searchInput.value?.trim() || "";
        const wordCount = trimmed === "" ? 0 : trimmed.split(/\s+/).length;

        container.classList.remove(
          "bn-input-empty",
          "bn-input-typing",
          "bn-input-long-search"
        );

        if (!wordCount) {
          container.classList.add("bn-input-empty");
        } else if (wordCount <= longSearchWordCount) {
          container.classList.add("bn-input-typing");
        } else {
          container.classList.add("bn-input-long-search");
        }
      }
    });
  }, [input]);

  useEffect(() => {
    if (!autocompleteConfigs?.enable) {
      return;
    }

    const history =
      tenant_id === "928010a0-63dc-4fc1-a3fa-4f0547de66ce"
        ? JSON.parse(localStorage.getItem(BNLY_SEARCH_HISTORY)) || []
        : [];
    const historySuggestions = input
      ? history?.filter(({ label }) =>
          label?.toLowerCase().includes(input?.toLowerCase())
        )
      : history;

    if (!input?.length && !historySuggestions.length) {
      setSuggestedTerms([]);
    }

    const MIN_INPUT_LENGTH = 2;

    const getSuggestions = async () => {
      try {
        if (input?.length >= MIN_INPUT_LENGTH) {
          const { data } = await searchApiService.getAutocompleteSuggestions({
            input,
            tenant_id,
          });

          const redirectSuggestions = filterRedirectSuggestions(
            input,
            redirectRules
          );

          const suggestions = [
            ...historySuggestions,
            ...data,
            ...redirectSuggestions,
          ];

          if (suggestions) {
            setSuggestedTerms(suggestions);
          }
        } else {
          setSuggestedTerms([...historySuggestions]);
        }
      } catch (e) {
        console.error("[Beonly Search] - Get suggestions failed with", e);
      }
    };

    const timer = setTimeout(() => getSuggestions(), 350);
    return () => clearTimeout(timer);
  }, [input, tenant_id]);

  useEffect(() => {
    if (isFocused && suggestedTerms.length) {
      const eventProducts = suggestedTerms.map(({ label, params }) => {
        return {
          name: label,
          sku: params?.sku,
          product_id: params?.product_id,
          price_from: params?.prices?.price_from,
          price_to: params?.prices?.price_to,
        };
      });

      dispatchGA4SearchEvent({
        eventAction: "suggest",
        eventLabel: input || "Estágio inicial",
        eventCount: suggestedTerms.length,
        eventProducts,
      });
    }
  }, [suggestedTerms, isFocused]);

  const clearSearch = () => {
    setInput("");
  };

  const normalizeAnchors = () => {
    if (typeof anchorsApplied === "string") {
      return `&anchors[]=${anchorsApplied}`;
    } else if (anchorsApplied.length > 0) {
      const nAnchors = [];
      anchorsApplied.map((anchor) => {
        if (anchor.tree_key) {
          return nAnchors.push(`&anchors[]=${anchor.tree_key}`);
        } else return nAnchors.push(`&anchors[]=${anchor}`);
      });
      const fAnchors = nAnchors.join("");
      return fAnchors;
    } else {
      return "";
    }
  };

  const onSearch = async (e, searchE = "") => {
    if (e && e !== "forceSubmit" && e.hasOwnProperty("nativeEvent")) {
      e.preventDefault();
    }

    const sanitizedInput = input.replace("%", "");

    const redirectRule = findRedirectRule(input, redirectRules);
    if (redirectRule?.url) {
      saveSearchActionOnStorage({
        label: redirectRule.label || input,
        type: "redirect",
        url: redirectRule.url,
      });
      return (window.location.href = redirectRule.url);
    }

    if (onSubmit && (searchE || input) && e) {
      dispatchGA4SearchEvent({
        eventAction: "search",
        eventLabel: input,
        eventValue: `${e === "forceSubmit" ? "voice" : "text"}`,
      });

      let encodedUrl;
      if (useAnchors && tenant_id !== "928010a0-63dc-4fc1-a3fa-4f0547de66ce") {
        const anchors = normalizeAnchors();
        encodedUrl = encodeURI(
          `/${search_page_path}${
            search_page_path.includes("?") ? "&" : "?"
          }${searchKey}=${sanitizedInput}${anchors}`
        );
      } else {
        encodedUrl = encodeURI(
          `/${search_page_path}${
            search_page_path.includes("?") ? "&" : "?"
          }${searchKey}=${sanitizedInput}`
        );
      }

      saveSearchActionOnStorage({
        label: input,
        type: "submit",
        url: window.location.origin + encodedUrl,
      });

      return (window.location.href = window.location.origin + encodedUrl);
    }

    if (input || searchE) {
      const newInput = { input: input ? input : searchE };
      setSearch({ ...newInput });
    }

    if (useAnchors) {
      const anchorsTreeKeys = anchorsApplied.map(({ tree_key }) => tree_key);
      setAnchor(anchorsTreeKeys);

      if (anchorsTreeKeys.length) {
        if (tenant_id === "928010a0-63dc-4fc1-a3fa-4f0547de66ce")
          onAnchorSearch(anchorsTreeKeys[0]);
      }
    } else {
      setAnchor("");
    }

    window.location.hash = "";
  };

  const onAnchorSearch = (value, label) => {
    const encodedUrl = encodeURI(
      `/${search_page_path}${
        search_page_path.includes("?") ? "&" : "?"
      }${searchKey}=&anchors[]=${value}`
    );

    saveSearchActionOnStorage({
      label,
      type: "anchor",
      url: window.location.origin + encodedUrl,
    });

    return (window.location.href = window.location.origin + encodedUrl);
  };

  const onSearchValue = ({ action, label, target, type, url, value }) => {
    switch (action) {
      case "anchor":
        dispatchGA4SearchEvent({
          eventAction: "suggest_hit",
          eventLabel: `${label} (${value?.split(":")[0]}) (catalog)`,
          eventValue: "text",
        });

        onAnchorSearch(value, label);
        break;

      case "url":
        dispatchGA4SearchEvent({
          eventAction: "suggest_hit",
          eventLabel: `${label} (redirect)`,
          eventValue: "text",
        });

        saveSearchActionOnStorage({
          label,
          type: "url",
          url,
        });

        if (target && target === "_blank") {
          window.open(url);
        } else {
          window.location.href = url;
        }
        break;

      default:
        break;
    }
  };

  const saveSearchActionOnStorage = ({ label, type, url }) => {
    try {
      if (!label || !url) return;

      const history =
        JSON.parse(localStorage.getItem(BNLY_SEARCH_HISTORY)) || [];

      const alreadyInHistoryIndex = history.findIndex(
        (historyItem) =>
          historyItem.label === label && historyItem.type === type
      );

      if (alreadyInHistoryIndex !== -1) {
        const [historyItem] = history.splice(alreadyInHistoryIndex, 1);
        history.unshift(historyItem);
      } else {
        history.unshift({
          _id: crypto.randomUUID(),
          action: "history",
          label,
          url,
          type,
        });
      }

      if (history.length > 5) {
        history.pop();
      }

      localStorage.setItem(BNLY_SEARCH_HISTORY, JSON.stringify(history));
    } catch (e) {
      console.warn(
        "[Bnly Search] Save search action on storage failed with",
        e
      );
    }
  };

  return (
    <InputContainer
      className="bnly-search__input-container"
      {...inputConfigs}
      ref={inputRef}
    >
      <CustomForm
        onSubmit={onSearch}
        className="bn-search__field"
        style={noTerm && { ...noTermStyle }}
        {...inputConfigs}
        isFocused={isFocused}
        isAncored={anchorsApplied?.length}
        isAutocomplete={suggestedTerms?.length}
      >
        {voiceConfigs?.enable && (
          <SpeechComponent
            onText={setInput}
            onFinish={() => onSearch("forceSubmit")}
          />
        )}

        <CustomInput
          className="bn-search__input"
          onChange={({ target: { value } }) => setInput(value)}
          placeholder={placeholder}
          value={input ? decodeURIComponent(encodeURI(input)) : ""}
          {...inputConfigs}
          inputMode="search"
        />

        <AnchorCheckbox isFocused={isFocused} />

        {!detachedAutocomplete && autocompleteConfigs?.enable && (
          <Autocomplete
            inputValue={input}
            isFocused={isFocused}
            onSearch={() => onSearch("forceSubmit")}
            onSearchValue={(v) => onSearchValue(v)}
            suggestedTerms={suggestedTerms}
            saveSearchActionOnStorage={saveSearchActionOnStorage}
          />
        )}

        <CustomClearIcon
          alt="Limpar"
          className="bn-search__clear"
          onClick={() => clearSearch()}
          style={{ display: input ? "block" : "none" }}
        />

        <CustomSearchIcon
          type="submit"
          alt="search"
          {...inputConfigs}
          className="bn-search__submit"
        />
      </CustomForm>

      {detachedAutocomplete && autocompleteConfigs?.enable && (
        <Autocomplete
          inputValue={input}
          isFocused={isFocused}
          onSearch={() => onSearch("forceSubmit")}
          onSearchValue={(v) => onSearchValue(v)}
          suggestedTerms={suggestedTerms}
          saveSearchActionOnStorage={saveSearchActionOnStorage}
        />
      )}
    </InputContainer>
  );
};

export default SearchInput;
